import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  ImageIcon,
  Loader2,
  RotateCw,
  Settings2,
  Tag,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEbayConnection } from '@/hooks/useEbayConnection';
import { useBulkEbayList } from '@/hooks/useBulkEbayList';
import { JobProgress } from '@/components/ui/JobProgress';
import { useEbayTemplates } from '@/hooks/useEbayTemplates';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { cn } from '@/lib/utils';
import { UserItem } from '@/types/userItem';
import { hasOnlyStockPhoto } from '@/lib/listingEligibility';
import { EbayListingDialog } from './EbayListingDialog';
import { toast } from '@/lib/toast';
import { formatUsdCents } from '@/lib/formatMoney';

interface Props {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  items: UserItem[];
  onDone?: () => void;
}

type RowState = 'ready' | 'no_price' | 'no_photo' | 'already_listed' | 'not_listed';

interface PreparedRow {
  item: UserItem;
  state: RowState;
  reason?: string;
}

interface ItemSettings {
  ebay_template_id: string | null;
  ebay_best_offer_enabled: boolean | null;
  ebay_list_quantity: number | null;
  quantity: number | null;
  ebay_category_id: string | null;
  ebay_listing_format: 'fixed_price' | 'auction' | null;
  ebay_auction_duration_days: number | null;
  ebay_auction_start_cents: number | null;
}

function prepare(items: UserItem[], activeListingIds: Set<string>): PreparedRow[] {
  return items.map((item) => {
    if (activeListingIds.has(item.id)) {
      return { item, state: 'already_listed', reason: 'Already listed' };
    }
    // Require CardSimp listing before eBay publish — keeps both storefronts in sync.
    if (item.visibility !== 'listed') {
      return { item, state: 'not_listed', reason: 'Not listed on CardSimp' };
    }
    if (!item.asking_price_cents || item.asking_price_cents <= 0) {
      return { item, state: 'no_price', reason: 'No price' };
    }
    if (hasOnlyStockPhoto(item as any)) {
      return { item, state: 'no_photo', reason: 'Stock photo only' };
    }
    const imgs = (item as any).images ?? [];
    if (!imgs || imgs.length === 0) {
      return { item, state: 'no_photo', reason: 'No photo' };
    }
    return { item, state: 'ready' };
  });
}

export function BulkListOnEbayDialog({ open, onOpenChange, items, onDone }: Props) {
  const isMobile = useIsMobile();
  const { keyboardHeight: kbInset } = useVisualViewportInset();
  const queryClient = useQueryClient();
  const { data: ebayConn } = useEbayConnection();
  const isConnected = !!ebayConn?.is_active;
  const { rows, isRunning, done, total, run, retry, reset } = useBulkEbayList();
  const { list: templatesQuery } = useEbayTemplates();
  const templates = templatesQuery.data || [];

  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchTemplateId, setBatchTemplateId] = useState<string>('__keep__');
  const [batchOboMode, setBatchOboMode] = useState<'__keep__' | 'on' | 'off'>('__keep__');
  const [batchQtyMode, setBatchQtyMode] = useState<'__keep__' | 'stock' | 'fixed'>('__keep__');
  const [batchFixedQty, setBatchFixedQty] = useState<string>('1');
  const [batchFormat, setBatchFormat] = useState<'__keep__' | 'fixed_price' | 'auction'>('__keep__');
  const [batchAuctionDuration, setBatchAuctionDuration] = useState<'1' | '3' | '5' | '7' | '10'>('7');
  const [batchAuctionStartMode, setBatchAuctionStartMode] = useState<'asking' | 'fixed'>('asking');
  const [batchAuctionStartDollars, setBatchAuctionStartDollars] = useState<string>('');
  const [batchApplying, setBatchApplying] = useState(false);


  const itemIds = items.map((i) => i.id);

  // Existing active/pending listings → "already_listed" state
  const { data: existingListings } = useQuery({
    queryKey: ['ebay-listings-for-bulk', itemIds.sort().join(',')],
    enabled: open && itemIds.length > 0,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('ebay_listings')
        .select('item_id,status')
        .in('item_id', itemIds);
      if (error) throw error;
      return (data ?? []) as { item_id: string; status: string }[];
    },
  });

  // Pull saved per-item eBay settings to render meta chips
  const { data: itemSettingsList } = useQuery({
    queryKey: ['ebay-bulk-item-settings', itemIds.sort().join(',')],
    enabled: open && itemIds.length > 0,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('user_items')
        .select('id,ebay_template_id,ebay_best_offer_enabled,ebay_list_quantity,quantity,ebay_category_id,ebay_listing_format,ebay_auction_duration_days,ebay_auction_start_cents')
        .in('id', itemIds);
      if (error) throw error;
      return data as Array<{ id: string } & ItemSettings>;
    },
  });

  const settingsById = useMemo(() => {
    const m = new Map<string, ItemSettings>();
    (itemSettingsList ?? []).forEach((r) => m.set(r.id, r));
    return m;
  }, [itemSettingsList]);

  const templateNameById = useMemo(() => {
    const m = new Map<string, string>();
    templates.forEach((t) => m.set(t.id, t.name));
    return m;
  }, [templates]);

  const activeListingIds = useMemo(() => {
    const s = new Set<string>();
    (existingListings ?? []).forEach((r) => {
      if (r.status === 'active' || r.status === 'pending') s.add(r.item_id);
    });
    return s;
  }, [existingListings]);

  const prepared = useMemo(() => prepare(items, activeListingIds), [items, activeListingIds]);

  const readyItems = prepared.filter((p) => p.state === 'ready' && !skipped.has(p.item.id));
  const needFixCount = prepared.filter((p) => p.state !== 'ready').length;
  const readyCount = readyItems.length;

  const successCount = Object.values(rows).filter((r) => r.status === 'listed').length;
  const failedCount = Object.values(rows).filter((r) => r.status === 'failed').length;
  const hasRunResults = total > 0;

  const handleClose = (next: boolean) => {
    if (isRunning) return;
    if (!next) {
      reset();
      setSkipped(new Set());
      setBatchOpen(false);
      setBatchTemplateId('__keep__');
      setBatchOboMode('__keep__');
      setBatchQtyMode('__keep__');
      setBatchFormat('__keep__');
      onOpenChange(false);
      if (hasRunResults && successCount > 0) onDone?.();
    } else {
      onOpenChange(next);
    }
  };

  const handleConfirm = async () => {
    if (readyItems.length === 0) return;
    await run({
      itemIds: readyItems.map((r) => r.item.id),
      onOpen: () => onOpenChange(true),
    });
  };

  const toggleInclude = (id: string) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyBatchDefaults = async () => {
    if (readyItems.length === 0) return;
    const patch: Record<string, any> = {};
    if (batchTemplateId !== '__keep__') {
      patch.ebay_template_id = batchTemplateId === '__none__' ? null : batchTemplateId;
    }
    if (batchOboMode !== '__keep__') {
      patch.ebay_best_offer_enabled = batchOboMode === 'on';
      if (batchOboMode === 'off') {
        patch.ebay_auto_accept_cents = null;
        patch.ebay_auto_decline_cents = null;
      }
    }
    if (batchFormat === 'fixed_price') {
      patch.ebay_listing_format = 'fixed_price';
      patch.ebay_auction_start_cents = null;
      patch.ebay_auction_reserve_cents = null;
      patch.ebay_auction_bin_cents = null;
      patch.ebay_auction_duration_days = null;
    } else if (batchFormat === 'auction') {
      patch.ebay_listing_format = 'auction';
      patch.ebay_auction_duration_days = parseInt(batchAuctionDuration, 10);
      // Auctions list as a single unit
      patch.ebay_list_quantity = 1;
      patch.ebay_best_offer_enabled = false;
    }
    const nothingPicked =
      Object.keys(patch).length === 0 &&
      batchQtyMode === '__keep__' &&
      batchFormat === '__keep__';
    if (nothingPicked) {
      toast.message('No batch defaults selected');
      return;
    }
    setBatchApplying(true);
    try {
      const ids = readyItems.map((r) => r.item.id);
      if (Object.keys(patch).length > 0) {
        const { error } = await (supabase as any).from('user_items').update(patch).in('id', ids);
        if (error) throw error;
      }
      // Auction start price: per-item, since "use asking price" varies per row
      if (batchFormat === 'auction') {
        if (batchAuctionStartMode === 'asking') {
          for (const r of readyItems) {
            const start = r.item.asking_price_cents;
            if (start && start > 0) {
              await (supabase as any)
                .from('user_items')
                .update({ ebay_auction_start_cents: start })
                .eq('id', r.item.id);
            }
          }
        } else {
          const dollars = parseFloat(batchAuctionStartDollars);
          if (!Number.isFinite(dollars) || dollars <= 0) {
            toast.error('Enter a valid auction start price');
            setBatchApplying(false);
            return;
          }
          const cents = Math.round(dollars * 100);
          const { error } = await (supabase as any)
            .from('user_items')
            .update({ ebay_auction_start_cents: cents })
            .in('id', ids);
          if (error) throw error;
        }
      }
      if (batchQtyMode === 'stock') {
        for (const r of readyItems) {
          const stock = settingsById.get(r.item.id)?.quantity ?? 1;
          await (supabase as any)
            .from('user_items')
            .update({ ebay_list_quantity: stock })
            .eq('id', r.item.id);
        }
      } else if (batchQtyMode === 'fixed') {
        const n = Math.max(1, parseInt(batchFixedQty, 10) || 1);
        const { error } = await (supabase as any)
          .from('user_items')
          .update({ ebay_list_quantity: n })
          .in('id', ids);
        if (error) throw error;
      }
      await queryClient.invalidateQueries({ queryKey: ['ebay-bulk-item-settings'] });
      toast.success(`Applied to ${ids.length} ${ids.length === 1 ? 'item' : 'items'}`);
      setBatchTemplateId('__keep__');
      setBatchOboMode('__keep__');
      setBatchQtyMode('__keep__');
      setBatchFormat('__keep__');
    } catch (e: any) {
      toast.error(`Could not apply: ${e?.message ?? e}`);
    } finally {
      setBatchApplying(false);
    }
  };


  const titleNode = hasRunResults
    ? `Listed ${successCount} of ${total}${failedCount ? ` · ${failedCount} failed` : ''}`
    : `List ${items.length} ${items.length === 1 ? 'item' : 'items'} on eBay`;

  const notConnectedBlock = !isConnected && (
    <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
      <p className="font-medium text-warning">Connect eBay first</p>
      <p className="text-muted-foreground mt-1">
        Finish the eBay setup before bulk listing.{' '}
        <Link to="/settings" className="text-active-gold underline underline-offset-2">
          Open Profile → eBay
        </Link>
      </p>
    </div>
  );

  const summaryRow = !hasRunResults && (
    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
      <Badge variant="outline" className="bg-success/10 text-success border-success/30">
        {readyCount} ready
      </Badge>
      {needFixCount > 0 && (
        <Badge variant="outline" className="bg-white/[0.04] border-white/10 text-muted-foreground">{needFixCount} need fixes</Badge>
      )}
      {skipped.size > 0 && (
        <Badge variant="outline" className="bg-white/[0.04] border-white/10 text-muted-foreground">{skipped.size} excluded</Badge>
      )}
    </div>
  );

  const helperLine = !hasRunResults && (
    <p className="text-[11px] text-muted-foreground">
      Toggle <span className="text-foreground/80">Include</span> off to leave an item out of this batch (it stays in your inventory). Tap <span className="text-foreground/80">Edit</span> to open the full eBay editor for that item — description, category, OBO, quantity.
    </p>
  );

  const batchDefaults = !hasRunResults && readyCount > 0 && (
    <Collapsible open={batchOpen} onOpenChange={setBatchOpen} className="rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left">
        <div className="flex items-center gap-2 text-sm">
          <Settings2 className="w-4 h-4 text-active-gold" />
          <span className="font-medium">Batch defaults</span>
          <span className="text-xs text-muted-foreground">· applies to {readyCount} ready</span>
        </div>
        <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform', batchOpen && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 space-y-3 border-t border-white/[0.06] pt-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Listing format</Label>
          <Select value={batchFormat} onValueChange={(v) => setBatchFormat(v as any)}>
            <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__keep__" className="text-xs">Keep each item's current</SelectItem>
              <SelectItem value="fixed_price" className="text-xs">Fixed price for all</SelectItem>
              <SelectItem value="auction" className="text-xs">Auction for all</SelectItem>
            </SelectContent>
          </Select>
          {batchFormat === 'auction' && (
            <div className="space-y-2 pt-1 pl-2 border-l border-active-gold/30">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Duration</Label>
                <Select value={batchAuctionDuration} onValueChange={(v) => setBatchAuctionDuration(v as any)}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(['1', '3', '5', '7', '10'] as const).map((d) => (
                      <SelectItem key={d} value={d} className="text-xs">{d} {d === '1' ? 'day' : 'days'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Start price</Label>
                <div className="flex items-center gap-2">
                  <Select value={batchAuctionStartMode} onValueChange={(v) => setBatchAuctionStartMode(v as any)}>
                    <SelectTrigger className="h-9 text-xs flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asking" className="text-xs">Use each item's asking price</SelectItem>
                      <SelectItem value="fixed" className="text-xs">Same start price…</SelectItem>
                    </SelectContent>
                  </Select>
                  {batchAuctionStartMode === 'fixed' && (
                    <Input
                      type="number"
                      min={0.01}
                      step={0.01}
                      placeholder="$"
                      value={batchAuctionStartDollars}
                      onChange={(e) => setBatchAuctionStartDollars(e.target.value)}
                      className="h-9 w-24 text-xs"
                    />
                  )}
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">Auctions list as a single unit and ignore Best Offer. Set Reserve / Buy It Now per item in Edit.</p>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Description template</Label>
          <Select value={batchTemplateId} onValueChange={setBatchTemplateId}>
            <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__keep__" className="text-xs">Keep each item's current</SelectItem>
              <SelectItem value="__none__" className="text-xs">No template — use editor on item</SelectItem>
              {templates.filter((t) => t.scope === 'preset').length > 0 && (
                <>
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Presets</div>
                  {templates.filter((t) => t.scope === 'preset').map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
                  ))}
                </>
              )}
              {templates.filter((t) => t.scope === 'user').length > 0 && (
                <>
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Your templates</div>
                  {templates.filter((t) => t.scope === 'user').map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Best Offer (OBO)</Label>
          <Select value={batchOboMode} onValueChange={(v) => setBatchOboMode(v as any)}>
            <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__keep__" className="text-xs">Keep each item's current</SelectItem>
              <SelectItem value="on" className="text-xs">Turn on for all</SelectItem>
              <SelectItem value="off" className="text-xs">Turn off for all</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground">Auto-accept / decline thresholds stay per-item — set them in Edit.</p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Quantity to list</Label>
          <div className="flex items-center gap-2">
            <Select value={batchQtyMode} onValueChange={(v) => setBatchQtyMode(v as any)}>
              <SelectTrigger className="h-9 text-xs flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__keep__" className="text-xs">Keep each item's current</SelectItem>
                <SelectItem value="stock" className="text-xs">Match stock quantity</SelectItem>
                <SelectItem value="fixed" className="text-xs">Same fixed quantity…</SelectItem>
              </SelectContent>
            </Select>
            {batchQtyMode === 'fixed' && (
              <Input
                type="number"
                min={1}
                value={batchFixedQty}
                onChange={(e) => setBatchFixedQty(e.target.value)}
                className="h-9 w-20 text-xs"
              />
            )}
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          onClick={applyBatchDefaults}
          disabled={batchApplying || (batchTemplateId === '__keep__' && batchOboMode === '__keep__' && batchQtyMode === '__keep__' && batchFormat === '__keep__')}
          className="bg-active-gold/95 hover:bg-active-gold text-black h-8"
        >
          {batchApplying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply to all ready'}
        </Button>
      </CollapsibleContent>
    </Collapsible>
  );

  const list = (
    <div className="space-y-1.5">
      {prepared.map(({ item, state, reason }) => {
        const runState = rows[item.id];
        const isExcluded = skipped.has(item.id);
        const img = (item as any).images?.[0]?.image_url || (item as any).stock_image_url;
        const priceLabel = item.asking_price_cents
          ? `${formatUsdCents(item.asking_price_cents)}`
          : '—';
        const settings = settingsById.get(item.id);
        const tplName = settings?.ebay_template_id
          ? templateNameById.get(settings.ebay_template_id) || 'Custom template'
          : 'No template';
        const listQty = settings?.ebay_list_quantity ?? settings?.quantity ?? 1;
        const obo = !!settings?.ebay_best_offer_enabled;
        const isAuction = settings?.ebay_listing_format === 'auction';
        const auctionDays = settings?.ebay_auction_duration_days ?? 7;
        const canEdit = state === 'ready';

        let statusEl;
        if (runState?.status === 'listed') {
          statusEl = (
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <CheckCircle2 className="w-3.5 h-3.5" /> Listed
            </span>
          );
        } else if (runState?.status === 'listing') {
          statusEl = (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Listing…
            </span>
          );
        } else if (runState?.status === 'failed') {
          statusEl = (
            <span className="inline-flex items-center gap-1 text-xs text-destructive" title={runState.error}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate max-w-[180px]">{runState.error?.slice(0, 60) || 'Failed'}</span>
            </span>
          );
        } else if (runState?.status === 'queued') {
          statusEl = <span className="text-xs text-muted-foreground">Queued</span>;
        } else if (state !== 'ready') {
          statusEl = (
            <span className="inline-flex items-center gap-1 text-xs text-warning min-w-0" title={reason}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate min-w-0">{reason}</span>
            </span>
          );
        }

        return (
          <div
            key={item.id}
            className={cn(
              'rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5 min-w-0',
              isExcluded && 'opacity-50'
            )}
          >
            <div className="grid w-full min-w-0 grid-cols-[3rem_minmax(0,1fr)] gap-x-3 gap-y-2 lg:grid-cols-[3rem_minmax(0,1fr)_auto]">
              <div className="col-start-1 row-start-1 w-12 h-12 rounded-md bg-black/40 overflow-hidden flex items-center justify-center">
                {img ? (
                  <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
              <div className="col-start-2 row-start-1 min-w-0 overflow-hidden">
                <p className="text-sm font-medium truncate">{item.title}</p>
                <p className="text-[11px] text-muted-foreground tabular-nums truncate">
                  <span className="text-foreground/80">{priceLabel}</span>
                  {item.set_name ? ` · ${item.set_name}` : ''}
                </p>
                {canEdit && (
                  <div className="mt-1 flex min-w-0 items-center gap-1.5 overflow-hidden text-[10px]">
                    <span className="inline-flex min-w-0 items-center gap-1 px-1.5 py-0.5 rounded bg-white/[0.04] text-muted-foreground">
                      <Tag className="w-3 h-3 shrink-0" /> <span className="truncate">{tplName}</span>
                    </span>
                    {isAuction ? (
                      <span className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 rounded bg-active-gold/10 text-active-gold border border-active-gold/30">
                        Auction · {auctionDays}d
                      </span>
                    ) : (
                      <>
                        <span className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 rounded bg-white/[0.04] text-muted-foreground">
                          Fixed · Qty {listQty}
                        </span>
                        {obo && (
                          <span className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 rounded bg-active-gold/10 text-active-gold border border-active-gold/30">
                            OBO
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              {statusEl && (
                <div className="col-start-2 row-start-2 min-w-0 max-w-full justify-self-start overflow-hidden lg:col-start-3 lg:row-start-1 lg:max-w-[180px] lg:justify-self-end lg:text-right">{statusEl}</div>
              )}

              {/* Action row */}
              <div className="col-start-2 row-start-3 flex min-w-0 items-center justify-between gap-2 pr-1 lg:col-end-4 lg:row-start-2">
              <div className="flex items-center gap-2">
                {runState?.status === 'failed' && (
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => retry(item.id)} disabled={isRunning} title="Retry">
                    <RotateCw className="w-3.5 h-3.5 mr-1" /> Retry
                  </Button>
                )}
                {runState?.status === 'listed' && runState.view_url && (
                  <a
                    href={runState.view_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs text-active-gold hover:bg-white/[0.06]"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View
                  </a>
                )}
                {!hasRunResults && canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs bg-white/[0.05]"
                    onClick={() => setEditingItemId(item.id)}
                  >
                    Edit
                  </Button>
                )}
              </div>
              {!hasRunResults && canEdit && (
                <div className="flex items-center gap-2">
                  <Label htmlFor={`include-${item.id}`} className="text-[11px] text-muted-foreground cursor-pointer">
                    Include
                  </Label>
                  <Switch
                    id={`include-${item.id}`}
                    checked={!isExcluded}
                    onCheckedChange={() => toggleInclude(item.id)}
                    className="data-[state=checked]:bg-active-gold"
                  />
                </div>
              )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const progress = (isRunning || hasRunResults) && (
    <JobProgress
      label={isRunning ? 'Listing on eBay' : 'Listed on eBay'}
      done={done}
      total={total}
      failed={failedCount}
      isRunning={isRunning}
    />
  );

  const actions = (
    <>
      <Button variant="outline" onClick={() => handleClose(false)} disabled={isRunning} className="flex-1 sm:flex-none">
        {hasRunResults ? 'Close' : 'Cancel'}
      </Button>
      {!hasRunResults && (
        <Button
          onClick={handleConfirm}
          disabled={!isConnected || readyCount === 0 || isRunning}
          className="bg-active-gold/95 hover:bg-active-gold text-black flex-1 sm:flex-none"
        >
          {isRunning ? `Listing… ${done}/${total}` : `List ${readyCount} on eBay`}
        </Button>
      )}
    </>
  );

  // Stacked single-item editor — opens INLINE on top of bulk surface, no nav
  const editingItem = editingItemId ? items.find((i) => i.id === editingItemId) : null;
  const editorEl = editingItem && (
    <EbayListingDialog
      itemId={editingItem.id}
      hasPrice={!!editingItem.asking_price_cents && editingItem.asking_price_cents > 0}
      open={!!editingItemId}
      deferList
      onOpenChange={(o) => {
        if (!o) {
          setEditingItemId(null);
          // refresh meta chips so they reflect saved changes
          queryClient.invalidateQueries({ queryKey: ['ebay-bulk-item-settings'] });
          queryClient.invalidateQueries({ queryKey: ['ebay-listings-for-bulk'] });
        }
      }}
    />
  );

  if (isMobile) {
    return (
      <>
        <Sheet open={open} onOpenChange={handleClose}>
          <SheetContent
            side="bottom"
            className="py-0 flex flex-col gap-0 h-[92dvh] max-h-[92dvh]"
            style={{ paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${kbInset}px)` }}
          >
            <SheetHeader className="px-0 pt-4 pb-2 text-left">
              <SheetTitle className="text-base">{titleNode}</SheetTitle>
            </SheetHeader>
            <div className="px-0 pb-2 space-y-2">
              {notConnectedBlock}
              {summaryRow}
              {helperLine}
              {batchDefaults}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-0 pb-2">{list}</div>
            <div className="px-0 pt-2">{progress}</div>
            <div className="flex flex-row gap-2 px-0 pt-3 pb-3 border-t border-white/[0.06]">{actions}</div>
          </SheetContent>
        </Sheet>
        {editorEl}
      </>
    );
  }

  return (
    <>
      {/* Desktop: opaque surface + pinned header/footer with a single inner
          scroll body. Glass + a page-height scroll made the blur repaint and
          leak flashes of the inventory grid behind it. */}
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="max-w-3xl sm:max-w-3xl p-0 gap-0 max-h-[85dvh] flex flex-col overflow-hidden sm:bg-[#121416] sm:backdrop-blur-none"
        >
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-white/[0.08]">
            <DialogTitle>{titleNode}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4 space-y-4 [transform:translateZ(0)]">
            {notConnectedBlock}
            {summaryRow}
            {helperLine}
            {batchDefaults}
            <div className="-mx-1 px-1">{list}</div>
          </div>
          <div className="shrink-0 border-t border-white/[0.08] px-6 py-3 space-y-3">
            {progress}
            <DialogFooter className="flex-row justify-end gap-2">{actions}</DialogFooter>
          </div>
        </DialogContent>

      </Dialog>
      {editorEl}
    </>
  );
}
